import { useEffect, useState } from 'react'
import Head from 'next/head'
import Script from 'next/script'
import Link from 'next/link'
import {
  OurClients,
  Migrate,
  Testimonials,
  Category,
  Footer,
  TryEdworking,
  EdworkingBanner,
  Header,
  EdworkingPromotion,
  IntroCompare,
  TopBanner,
  TaskFeatures,
} from '../../js/components'
import Image from 'next/image'
import { OrganizationSchema, WebPageSchema, BreadcrumbListSchema } from '../../js/schemas'
import path from 'path'
import fs from 'fs/promises' // or 'fs' if using callbacks
import { fetchWithRetry } from 'js/components'

export async function getStaticPaths({ locales }) {
  const array = locales.map((locale) => {
    return [
      { locale, params: { compare: 'edworking-vs-clickup' } },
      { locale, params: { compare: 'edworking-vs-asana' } },
      { locale, params: { compare: 'edworking-vs-airtable' } },
      { locale, params: { compare: 'edworking-vs-trello' } },
      { locale, params: { compare: 'edworking-vs-slack' } },
      { locale, params: { compare: 'edworking-vs-msteams' } },
      { locale, params: { compare: 'edworking-vs-monday' } },
      { locale, params: { compare: 'edworking-vs-notion' } },
      { locale, params: { compare: 'edworking-vs-noysi' } },
    ]
  })
  return {
    paths: array.flat(1),
    fallback: false,
  }
}

export async function getStaticProps({ locale, locales, params }) {
  const compare = params.compare?.substring(13, params.compare.length)
  if (compare === 'ms teams') {
    return {
      redirect: {
        destination: `/compare/edworking-vs-msteams`,
        statusCode: 301,
      },
    }
  } else if (compare === 'montag') {
    return {
      redirect: {
        destination: `/${locale}/compare/edworking-vs-monday`,
        statusCode: 301,
      },
    }
  }

  const data = await fetchWithRetry(process.env.NEXT_PUBLIC_VENUSAUR, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      operationName: 'GetBlogsApproved4',
      query:
        'query GetBlogsApproved4($language: String){getBlogsApproved4(language: $language){id rawTitle color title article category cover resume author { firstname lastname } createdAt}}',
      variables: { language: locale },
    }),
  })
  const blogs = data.data.getBlogsApproved4

  let content, features
  try {
    const filePath = path.join(process.cwd(), 'public', 'content', locale, 'compare.json')
    const filePath2 = path.join(process.cwd(), 'public', 'content', locale, 'features.json')

    // Read the file
    const jsonData = await fs.readFile(filePath, 'utf8')
    const jsonData2 = await fs.readFile(filePath2, 'utf8')
    content = JSON.parse(jsonData)
    features = JSON.parse(jsonData2)
  } catch (error) {
    console.error('error', error)
  }
  const allHighlights = Object.values(features.features).flatMap(
    (feature) => feature.highlights
  )
  const getRandomHighlights = (num) => {
    const shuffled = allHighlights.sort(() => 0.5 - Math.random())
    return shuffled.slice(0, num)
  }

  const highlights = getRandomHighlights(2)

  const company = content.compares.find((d) => d.name.toLocaleLowerCase() === compare)
  if (!company) {
    return {
      notFound: true,
    }
  }
  return {
    props: {
      content,
      highlights,
      features,
      compare: compare.charAt(0).toUpperCase() + compare.slice(1),
      company,
      blogs,
      locale,
      locales,
    },
  }
}

export default function Compare({
  compare,
  company,
  features,
  highlights,
  blogs,
  content,
  locale,
  locales,
}) {
  const [promotion, setPromotion] = useState(false)

  const onMouseOut = () => {
    const goon = localStorage.getItem('promotion')
    if (!goon) setPromotion(true)
    document.body.removeEventListener('mouseleave', onMouseOut)
  }

  useEffect(() => {
    setTimeout(function () {
      document.body.addEventListener('mouseleave', onMouseOut)
    }, 3000)
  }, [])

  return (
    <>
      <Script
        src="https://www.googletagmanager.com/gtag/js?id=UA-166939348-2"
        strategy="afterInteractive"
      />
      <Script id="google-analytics" strategy="afterInteractive">
        {`
            window.dataLayer = window.dataLayer || [];
            function gtag(){dataLayer.push(arguments);}
            gtag('js', new Date());
            
            gtag('config', 'UA-166939348-2');
        `}
      </Script>
      <Script id="google-tag-manager" strategy="afterInteractive">
        {`
          (function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
            new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
            j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
            'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
            })(window,document,'script','dataLayer','GTM-NRW96HD');
        `}
      </Script>
      <Script id="hotjar" strategy="afterInteractive">
        {`
          (function(h,o,t,j,a,r){
              h.hj=h.hj||function(){(h.hj.q=h.hj.q||[]).push(arguments)};
              h._hjSettings={hjid:2080824,hjsv:6};
              a=o.getElementsByTagName('head')[0];
              r=o.createElement('script');r.async=1;
              r.src=t+h._hjSettings.hjid+j+h._hjSettings.hjsv;
              a.appendChild(r);
          })(window,document,'https://static.hotjar.com/c/hotjar-','.js?sv=');
        `}
      </Script>
      <OrganizationSchema />
      <WebPageSchema
        name={`${content.head.title} vs ${company.name}`}
        description={`${content.head.description} Edworking vs ${company.name}`}
      />
      <BreadcrumbListSchema
        items={[
          {
            name: 'Home',
            url: `https://edworking.com/${locale !== 'en' ? `${locale}/` : ''}`,
          },
          {
            name: 'Compare',
            url: `https://edworking.com/${locale !== 'en' ? `${locale}/` : ''}compare`,
          },
          {
            name: `Edworking vs ${compare}`,
            url: `https://edworking.com/${
              locale !== 'en' ? `${locale}/` : ''
            }compare/edworking-vs-${compare.toLocaleLowerCase()}`,
          },
        ]}
      />
      <Head>
        <title>{`${content.head.title} vs ${company.name}`}</title>
        <meta itemProp="name" content={`${content.head.title} vs ${company.name}`} />
        <meta
          itemProp="description"
          content={`${content.head.description} Edworking vs ${company.name}`}
        />
        <meta
          name="description"
          content={`${content.head.description} Edworking vs ${company.name}`}
        />
        <link rel="shortcut icon" href="/images/favicon.ico" />
        {locales.map((locale1, index) => (
          <link
            key={index}
            rel="alternate"
            hrefLang={locale1}
            href={`https://edworking.com${
              locale1 !== 'en' ? `/${locale1}` : ''
            }/compare/edworking-vs-${compare.toLocaleLowerCase()}`}
          />
        ))}
        <link
          rel="alternate"
          href={`https://edworking.com/compare/edworking-vs-${compare.toLocaleLowerCase()}`}
          hrefLang="x-default"
        />
        <link
          rel="canonical"
          href={`https://edworking.com/${
            locale !== 'en' ? `${locale}/` : ''
          }compare/edworking-vs-${compare.toLocaleLowerCase()}`}
          key="canonical"
        />
      </Head>
      <noscript
        dangerouslySetInnerHTML={{
          __html: `<iframe src="https://www.googletagmanager.com/ns.html?id=GTM-NRW96HD" height="0" width="0" style="display:none;visibility:hidden"></iframe>`,
        }}
      />
      <EdworkingPromotion promotion={promotion} content={content.promotion} />
      {/*<TopBanner content={content.topbanner} />*/}
      <Header content={content.header} topbanner={true} />
      <IntroCompare content={content.compare} company={company} />
      <OurClients content={content.customers} />
      <ComparisonTable content={content.compare} company={company} />
      <FeaturesWidget content={content.features} />
      <EdworkingBanner content={features.edworkingbanner} disableButton />
      <Banner />
      <Migrate company={company.logo} content={content.migration} />
      <ComparePrice content={content.compare} company={company} />
      {/*<Testimonials content={content.reviews} />*/}
      <Integrations content={content.integrations} />
      <Category pagination={false} category={content.blogs} articles={blogs} />
      <TryEdworking content={content.endbanner} />
      <Footer content={content.footer} />
      <div id="livenoti" className="livenoti"></div>
    </>
  )
}
const Integrations = ({ content }) => {
  return (
    <section className="introlanding" style={{ width: '100%', marginBottom: 150 }}>
      <h1 className="title">{content.title}</h1>
      <h2 className="description">{content.description}</h2>
      <Image
        width={1200}
        height={500}
        className="integrations"
        style={{ width: '100%' }}
        src="/images/Integrations.png"
        alt="Edworking platform overview"
      />
      <div
        className="barcolors"
        style={{ maxWidth: '100vw', width: '100%', marginTop: 100 }}
      ></div>
    </section>
  )
}

const Banner = () => {
  const cta = 'Get Started Now',
    headline = 'All your work in one place',
    subheadline = 'All-in-one platform for your team and your work. Register now for Free.'
  return (
    <section className="banner" style={{ margin: '100px auto' }}>
      <Image
        width="150"
        height="150"
        className="image"
        src="https://storage.googleapis.com/dratini/Edworking%20Logo.png"
        alt=""
      />
      <div className="text">
        <div className="headline">{headline}</div>
        <div className="subheadline">{subheadline}</div>
      </div>
      <a className="blueButton" href="https://app.edworking.com/signup/user/">
        {cta}
      </a>
    </section>
  )
}

export const FeaturesWidget = ({ content }) => {
  const cardStyle = {
    boxShadow: '5px 50px 70px 10px rgba(71, 101, 107, 0.1)',
    backgroundColor: 'white',
    borderRadius: '8px',
    height: '300px',
    transition: 'background-color 0.3s ease',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    cursor: 'pointer',
  }

  const cardHoverStyle = {
    backgroundColor: '#F7F9FB',
  }

  const gridStyle = {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
    gap: '16px',
    width: '100%',
    padding: '16px',
    boxSizing: 'border-box',
    maxWidth: '1200px',
    margin: '0 auto',
  }

  const imageStyle = {
    width: '100%',
    height: '180px',
    objectFit: 'cover',
    objectPosition: 'center',
    borderRadius: '8px 8px 0 0',
    marginBottom: '16px',
  }

  const titleStyle = {
    fontSize: '20px',
    padding: '0 20px',
    width: '100%',
    fontWeight: 'bold',
    marginBottom: '8px',
  }

  const descriptionStyle = {
    fontSize: '14px',
    color: '#555',
    width: '100%',
    padding: '0 20px',
  }
  console.log('content', content)
  return (
    <div style={gridStyle}>
      {content?.map((item, index) => (
        <Link
          key={index}
          href={item.link}
          style={cardStyle}
          onMouseEnter={(e) =>
            (e.currentTarget.style.backgroundColor = cardHoverStyle.backgroundColor)
          }
          onMouseLeave={(e) =>
            (e.currentTarget.style.backgroundColor = cardStyle.backgroundColor)
          }
        >
          <img src={item.image} alt={item.title} style={imageStyle} />
          <div style={titleStyle}>{item.title}</div>
          <div style={descriptionStyle}>{item.description}</div>
        </Link>
      ))}
    </div>
  )
}

const ComparePrice = ({ company, content }) => {
  return (
    <section className="compare">
      <h2 className="title">{content.value}</h2>
      <h4
        className="description"
        style={{
          width: 600,
          margin: 'auto',
          marginBottom: 60,
          maxWidth: '100%',
        }}
      >
        {content.valuedesc}
      </h4>
      <div className="block">
        <div className="logo">
          <Image width={47} height={30} src="/images/Logo.svg" alt="" />
          <span>Edworking</span>
        </div>
        <div className="feature">
          <Image width={28} height={30} src="/images/Videocalls.svg" alt="" />
          <span>Videocalls</span>
        </div>
        <div className="feature">
          <Image width={26} height={30} src="/images/Tasks.svg" alt="" />
          <span>Task Management</span>
        </div>
        <div className="feature">
          <Image width={34} height={30} src="/images/Folder.svg" alt="" />
          <span>Files Sharing</span>
        </div>
        <div className="feature">
          <Image width={33} height={30} src="/images/Workspace.svg" alt="" />
          <span>Documents</span>
        </div>
        <div className="feature">
          <Image width={31} height={30} src="/images/Chat.svg" alt="" />
          <span>Chat</span>
        </div>
        <div className="feature">
          <Image width={26} height={30} src="/images/Mobile.svg" alt="" />
          <span>Mobile Application</span>
        </div>
        <div className="feature equal">
          <span>£4</span>
          <span>per user</span>
        </div>
      </div>
      <a
        className="blueButton"
        style={{ width: 200, margin: 50 }}
        href="https://edworking.com/pricing"
      >
        Pricing
      </a>
      <div className="multicolors" style={{ height: 5 }}></div>
      <div className="block">
        <div className="logo">
          <Image width={47} height={30} src={company.logo} alt="" style={{ width: 'auto' }} />
          <span>{company.name}</span>
        </div>
        {company.features.videocalls && (
          <div className="feature">
            <Image width={28} height={30} src="/images/Videocalls.svg" alt="" />
            <span>Videocalls</span>
          </div>
        )}
        {company.features.tasks && (
          <div className="feature">
            <Image width={26} height={30} src="/images/Tasks.svg" alt="" />
            <span>Task Management</span>
          </div>
        )}
        {company.features.tasks && (
          <div className="feature">
            <Image width={34} height={30} src="/images/Folder.svg" alt="" />
            <span>Files Sharing</span>
          </div>
        )}
        {company.features.documents && (
          <div className="feature">
            <Image width={33} height={30} src="/images/Workspace.svg" alt="" />
            <span>Documents</span>
          </div>
        )}
        {company.features.chat && (
          <div className="feature">
            <Image width={31} height={30} src="/images/Chat.svg" alt="" />
            <span>Chat</span>
          </div>
        )}
        {company.features.mobileapp && (
          <div className="feature">
            <Image width={26} height={30} src="/images/Mobile.svg" alt="" />
            <span>Mobile Application</span>
          </div>
        )}
        <div className="feature equal">
          <span>£{company.price}</span>
          <span>per user</span>
        </div>
      </div>
    </section>
  )
}

const ComparisonTable = ({ company, content }) => {
  return (
    <section className="compare">
      <h2 className="title">
        {content.table} {company.name}?
      </h2>
      <h4 className="description" style={{ marginBottom: 38, width: '100%', margin: 'auto' }}>
        {company.smallcompare}
      </h4>
      <table className="table">
        <thead>
          <tr>
            <th scope="col">
              <h5></h5>
            </th>
            <th scope="col">
              <h5>Edworking</h5>
            </th>
            <th scope="col">
              <h5>{company.name}</h5>
            </th>
          </tr>
        </thead>
        <tbody>
          {company.compare.map((compare, index) => (
            <tr key={index}>
              <td scope="col">
                <p>{compare.title}</p>
              </td>
              <td scope="col">
                {compare.edworking ? <Positive width={25} /> : <Negative width={20} />}
              </td>
              <td scope="col">
                {compare.competence ? <Positive width={25} /> : <Negative width={20} />}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </section>
  )
}

const Positive = ({ width }) => {
  return (
    <svg
      style={{ width }}
      xmlns="http://www.w3.org/2000/svg"
      version="1.1"
      id="Capa_1"
      x="0px"
      y="0px"
      viewBox="0 0 240.608 240.608"
    >
      <path
        style={{ fill: '#05d6a2' }}
        d="M208.789,29.972l31.819,31.82L91.763,210.637L0,118.876l31.819-31.82l59.944,59.942L208.789,29.972z"
      />
    </svg>
  )
}

const Negative = ({ width }) => {
  return (
    <svg
      style={{ width }}
      xmlns="http://www.w3.org/2000/svg"
      version="1.1"
      id="Capa_1"
      x="0px"
      y="0px"
      viewBox="0 0 180.607 180.607"
    >
      <path
        style={{ fill: '#cacaca' }}
        d="M180.607,10.607l-79.696,79.697l79.696,79.697L170,180.607l-79.696-79.696l-79.696,79.696L0,170.001l79.696-79.697L0,10.607  L10.607,0.001l79.696,79.696L170,0.001L180.607,10.607z"
      />
    </svg>
  )
}
